
import React, { useEffect, useRef, useState } from 'react';
import { Website, Section, Page } from '../types';
import { Icons } from '../constants';

interface VisualEditorProps {
  website: Website;
  activePage: Page;
  onUpdatePage: (updatedPage: Page) => void;
}

export const VisualEditor: React.FC<VisualEditorProps> = ({ website, activePage, onUpdatePage }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Selection state
  const [selectedElement, setSelectedElement] = useState<{
    target: HTMLElement;
    type: 'text' | 'image' | 'link';
    sectionId: string;
    rect: DOMRect;
  } | null>(null);

  // Sync scroll/resize to keep toolbar attached
  useEffect(() => {
    const updateRect = () => {
      if (selectedElement && selectedElement.target) {
        setSelectedElement(prev => prev ? { ...prev, rect: prev.target.getBoundingClientRect() } : null);
      }
    };
    window.addEventListener('scroll', updateRect, true);
    window.addEventListener('resize', updateRect);
    return () => {
      window.removeEventListener('scroll', updateRect, true);
      window.removeEventListener('resize', updateRect);
    };
  }, [selectedElement]);

  useEffect(() => {
    if (website.customCss) {
      const styleId = 'injected-site-styles';
      let styleTag = document.getElementById(styleId) as HTMLStyleElement;
      if (!styleTag) {
        styleTag = document.createElement('style');
        styleTag.id = styleId;
        document.head.appendChild(styleTag);
      }
      styleTag.innerHTML = website.customCss;
    }
  }, [website.customCss]);

  const syncChanges = (target: HTMLElement, sectionId: string) => {
    const sectionElement = target.closest('.section-container');
    if (sectionElement) {
      const cleanHtml = sectionElement.innerHTML;
      const updatedSections = activePage.sections.map(s => 
        s.id === sectionId ? { ...s, content: { ...s.content, html: cleanHtml } } : s
      );
      onUpdatePage({ ...activePage, sections: updatedSections });
    }
  };

  const handleContainerClick = (e: React.MouseEvent, sectionId: string) => {
    const target = e.target as HTMLElement;
    
    // 1. Detect Images (including background images)
    const img = target.closest('img');
    const hasBgImage = window.getComputedStyle(target).backgroundImage !== 'none';
    
    if (img || hasBgImage) {
      e.preventDefault();
      e.stopPropagation();
      const element = (img || target) as HTMLElement;
      setSelectedElement({ target: element, type: 'image', sectionId, rect: element.getBoundingClientRect() });
      return;
    }

    // 2. Detect Links
    const anchor = target.closest('a');
    if (anchor) {
      e.preventDefault();
      e.stopPropagation();
      setSelectedElement({ target: anchor as HTMLElement, type: 'link', sectionId, rect: anchor.getBoundingClientRect() });
      return;
    }

    // 3. Detect Text Nodes and editable regions
    const textTags = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'span', 'button', 'li', 'td', 'th', 'div'];
    if (textTags.includes(target.tagName.toLowerCase())) {
      // If already editing, don't interrupt
      if (target.getAttribute('contenteditable') === 'true') return;

      e.preventDefault();
      e.stopPropagation();

      setSelectedElement({ target, type: 'text', sectionId, rect: target.getBoundingClientRect() });
      
      target.setAttribute('contenteditable', 'true');
      target.classList.add('editing-active');
      target.focus();

      const handleBlur = () => {
        target.removeAttribute('contenteditable');
        target.classList.remove('editing-active');
        syncChanges(target, sectionId);
        target.removeEventListener('blur', handleBlur);
      };

      target.addEventListener('blur', handleBlur);
    } else {
      // Clicked on empty space or non-editable container
      setSelectedElement(null);
    }
  };

  const updateLink = (path: string) => {
    if (selectedElement && selectedElement.type === 'link') {
      selectedElement.target.setAttribute('href', path);
      syncChanges(selectedElement.target, selectedElement.sectionId);
      setSelectedElement(null);
    }
  };

  const updateImageSource = (src: string) => {
    if (selectedElement && selectedElement.type === 'image') {
      if (selectedElement.target.tagName.toLowerCase() === 'img') {
        (selectedElement.target as HTMLImageElement).src = src;
      } else {
        selectedElement.target.style.backgroundImage = `url(${src})`;
      }
      syncChanges(selectedElement.target, selectedElement.sectionId);
      setSelectedElement(null);
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && selectedElement?.type === 'image') {
      const reader = new FileReader();
      reader.onload = (event) => {
        const result = event.target?.result as string;
        updateImageSource(result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Helper for toolbar positioning
  const getToolbarStyle = () => {
    if (!selectedElement) return {};
    const { rect } = selectedElement;
    
    // Try to place above, if too high, place below
    let top = rect.top - 120;
    if (top < 100) top = rect.bottom + 20;

    // Center horizontally but keep within viewport
    let left = rect.left + (rect.width / 2) - 160;
    left = Math.max(20, Math.min(window.innerWidth - 340, left));

    return { top, left };
  };

  const getActiveImageSrc = () => {
    if (!selectedElement || selectedElement.type !== 'image') return '';
    if (selectedElement.target.tagName.toLowerCase() === 'img') {
      return (selectedElement.target as HTMLImageElement).src;
    }
    const bg = window.getComputedStyle(selectedElement.target).backgroundImage;
    return bg.replace(/url\(['"]?(.*?)['"]?\)/, '$1');
  };

  return (
    <div className="bg-white rounded-[3rem] shadow-2xl overflow-hidden border border-slate-100 min-h-[800px] relative" ref={containerRef}>
      {/* Browser Bar */}
      <div className="bg-slate-50 border-b border-slate-100 px-8 py-3 flex items-center justify-between z-20 relative select-none">
        <div className="flex items-center gap-2">
          <div className="w-2.5 h-2.5 rounded-full bg-rose-400 shadow-sm"></div>
          <div className="w-2.5 h-2.5 rounded-full bg-amber-400 shadow-sm"></div>
          <div className="w-2.5 h-2.5 rounded-full bg-emerald-400 shadow-sm"></div>
        </div>
        <div className="bg-white px-5 py-1.5 rounded-full text-[10px] font-bold text-slate-400 border border-slate-200 shadow-inner flex items-center gap-2">
          <Icons.Globe className="w-3 h-3" />
          {website.domain}{activePage.path}
        </div>
        <div className="w-12"></div>
      </div>

      {/* Editor Surface */}
      <div className="relative z-0">
        {activePage.sections.map((section) => (
          <div 
            key={section.id} 
            className="group relative section-container"
            onClick={(e) => handleContainerClick(e, section.id)}
          >
            <div 
              dangerouslySetInnerHTML={{ __html: section.content.html || "" }} 
              className="w-full h-full pointer-events-auto transition-opacity duration-300"
            />
          </div>
        ))}
      </div>

      {/* FLOATING TOOLBARS */}
      {selectedElement && (
        <>
          {/* Visual Highlight Overlay */}
          <div 
            className="fixed pointer-events-none border-4 border-indigo-500 rounded-lg z-[80] shadow-[0_0_0_9999px_rgba(15,23,42,0.1)] transition-all duration-300 ease-out"
            style={{
              top: selectedElement.rect.top - 6,
              left: selectedElement.rect.left - 6,
              width: selectedElement.rect.width + 12,
              height: selectedElement.rect.height + 12,
            }}
          />

          <div 
            className="fixed z-[100] animate-in fade-in zoom-in-95 slide-in-from-top-4 duration-300"
            style={getToolbarStyle()}
          >
            {selectedElement.type === 'link' && (
              <div className="bg-slate-900 text-white rounded-[2.5rem] shadow-2xl p-7 w-80 flex flex-col gap-5 border border-white/10 backdrop-blur-3xl ring-1 ring-white/10">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <Icons.Globe className="w-3 h-3 text-indigo-400" />
                    <span className="text-[10px] font-black uppercase tracking-[0.2em] text-indigo-400">Navigation</span>
                  </div>
                  <button onClick={() => setSelectedElement(null)} className="p-1.5 hover:bg-white/10 rounded-full transition-colors">
                    <Icons.Trash className="w-4 h-4 text-white/40 hover:text-rose-400" />
                  </button>
                </div>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <label className="text-[9px] font-bold text-slate-500 uppercase tracking-widest px-1">Connect to Page</label>
                    <div className="grid grid-cols-1 gap-1.5 max-h-48 overflow-y-auto custom-scrollbar pr-1">
                      {website.pages.map(p => (
                        <button 
                          key={p.path}
                          onClick={() => updateLink(p.path)}
                          className={`flex items-center justify-between px-4 py-3 rounded-2xl text-xs font-black transition-all border ${selectedElement.target.getAttribute('href') === p.path ? 'bg-indigo-600 border-indigo-500 text-white shadow-xl scale-102' : 'bg-white/5 border-white/5 hover:bg-white/10 text-slate-300'}`}
                        >
                          {p.name}
                          <span className="opacity-30 font-mono text-[9px]">{p.path}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="h-px bg-white/10"></div>
                  <div className="space-y-2">
                    <label className="text-[9px] font-bold text-slate-500 uppercase tracking-widest px-1">External Link</label>
                    <div className="flex gap-2">
                      <input 
                        className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-xs font-mono outline-none focus:ring-2 ring-indigo-500/50"
                        placeholder="https://..."
                        defaultValue={selectedElement.target.getAttribute('href') || ''}
                        onKeyDown={e => {
                          if (e.key === 'Enter') updateLink((e.target as HTMLInputElement).value);
                        }}
                      />
                      <button 
                        onClick={(e) => {
                          const val = (e.currentTarget.previousSibling as HTMLInputElement).value;
                          updateLink(val);
                        }}
                        className="bg-indigo-600 p-3 rounded-xl hover:bg-indigo-500 transition-all shadow-lg active:scale-90"
                      >
                         <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4"><polyline points="20 6 9 17 4 12"></polyline></svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {selectedElement.type === 'image' && (
              <div className="bg-slate-900 text-white rounded-[2.5rem] shadow-2xl p-7 w-80 flex flex-col gap-6 border border-white/10 backdrop-blur-3xl ring-1 ring-white/10">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-2">
                    <Icons.Plus className="w-3 h-3 text-indigo-400" />
                    <span className="text-[10px] font-black uppercase tracking-[0.2em] text-indigo-400">Media Hub</span>
                  </div>
                  <button onClick={() => setSelectedElement(null)} className="p-1.5 hover:bg-white/10 rounded-full transition-colors">
                    <Icons.Trash className="w-4 h-4 text-white/40 hover:text-rose-400" />
                  </button>
                </div>
                
                <div className="space-y-4">
                  <button 
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full bg-indigo-600 hover:bg-indigo-500 py-4.5 rounded-[1.5rem] flex items-center justify-center gap-3 font-black text-sm transition-all shadow-xl shadow-indigo-900/50 active:scale-95 border border-indigo-400/20"
                  >
                    <Icons.Plus className="w-5 h-5" /> Upload Image
                  </button>
                  <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handleFileUpload} />
                  
                  <div className="h-px bg-white/10 w-full my-4"></div>

                  <div className="space-y-2">
                    <label className="text-[9px] font-bold text-slate-500 uppercase tracking-widest px-1">Image URL</label>
                    <input 
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-4 text-xs font-mono outline-none focus:ring-2 ring-indigo-500/50"
                      placeholder="Paste remote link..."
                      defaultValue={getActiveImageSrc()}
                      onKeyDown={e => {
                        if (e.key === 'Enter') updateImageSource((e.target as HTMLInputElement).value);
                      }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-[70] cursor-default bg-black/5" 
            onClick={(e) => {
              if (e.target === e.currentTarget) setSelectedElement(null);
            }} 
          />
        </>
      )}

      {!selectedElement && (
        <div className="fixed bottom-12 left-1/2 -translate-x-1/2 bg-slate-900/90 backdrop-blur-2xl text-white px-10 py-5.5 rounded-[3rem] shadow-2xl flex items-center gap-6 z-50 animate-in slide-in-from-bottom-12 duration-500 border border-white/10 ring-1 ring-white/10">
          <div className="w-11 h-11 rounded-2xl bg-indigo-600 flex items-center justify-center shadow-2xl shadow-indigo-500/40 animate-pulse">
            <Icons.Sparkles className="w-6 h-6" />
          </div>
          <div>
            <p className="text-sm font-black tracking-tight leading-none">Visual Editor Active</p>
            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-[0.15em] mt-2 flex items-center gap-2.5">
              <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
              Click to modify any element
            </p>
          </div>
        </div>
      )}
      
      <style>{`
        .section-container { position: relative; }
        .section-container * {
           pointer-events: auto !important;
        }
        [contenteditable="true"] { cursor: text !important; outline: none !important; }
        [contenteditable="true"].editing-active {
          background: rgba(99, 102, 241, 0.08);
          box-shadow: 0 0 0 8px rgba(99, 102, 241, 0.3);
          border-radius: 8px;
        }
        img, [style*="background-image"] { 
          cursor: pointer !important; 
          transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275); 
        }
        img:hover, [style*="background-image"]:hover { 
          filter: brightness(0.9); 
          transform: scale(1.005);
          box-shadow: 0 0 0 6px rgba(99, 102, 241, 0.4); 
        }
        a { cursor: pointer !important; transition: all 0.3s ease; position: relative; }
        a:hover { box-shadow: 0 0 0 6px rgba(99, 102, 241, 0.25); border-radius: 6px; }
        .custom-scrollbar::-webkit-scrollbar { width: 6px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.15); border-radius: 10px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.25); }
      `}</style>
    </div>
  );
};
